import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import { Progress } from "./components/ui/progress";
import { toast, Toaster } from "sonner";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Context for user state
const UserContext = React.createContext();

const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('en');
  const [translations, setTranslations] = useState({});

  useEffect(() => {
    checkAuth();
    loadTranslations();
  }, []);

  useEffect(() => {
    loadTranslations();
  }, [language]);

  const checkAuth = async () => {
    try {
      // Check for session_id in URL fragment first
      const fragment = window.location.hash;
      const sessionIdMatch = fragment.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        await handleSocialAuth(sessionId);
        // Clean URL fragment
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      // Check existing session
      const token = localStorage.getItem('access_token');
      if (token) {
        const response = await api.get('/auth/me');
        setUser(response.data);
        setLanguage(response.data.preferred_language || "en");
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('access_token');
    }
    setLoading(false);
  };

  const handleSocialAuth = async (sessionId) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/social', { session_id: sessionId });
      login(response.data.user, response.data.access_token);
      toast.success('Successfully signed in with Google!');
    } catch (error) {
      console.error('Social auth failed:', error);
      toast.error('Social login failed');
    }
    setLoading(false);
  };

  const loadTranslations = async () => {
    try {
      const response = await axios.get(`${API}/translations/${language}`);
      setTranslations(response.data);
    } catch (error) {
      console.error('Failed to load translations:', error);
    }
  };

  const login = (userData, token) => {
    setUser(userData);
    localStorage.setItem('access_token', token);
    setLanguage(userData.preferred_language || "en");
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout failed:', error);
    }
    setUser(null);
    localStorage.removeItem('access_token');
  };

  const t = (key, fallback = key) => {
    return translations[key] || fallback;
  };

  return (
    <UserContext.Provider value={{ user, login, logout, loading, language, setLanguage, t }}>
      {children}
    </UserContext.Provider>
  );
};

const useUser = () => React.useContext(UserContext);

// API Helper
const api = axios.create({
  baseURL: API
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Banner images with language-specific selections
const getBannerImages = (language) => {
  // Default banner data - these will be enhanced with actual CMS data
  const baseImages = [
    {
      url: 'https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b',
      title: {
        en: 'Master AI & Business Development',
        de: 'Meistern Sie KI & Geschäftsentwicklung',
        fr: 'Maîtrisez l\'IA et le développement commercial',
        es: 'Domina la IA y el desarrollo empresarial',
        ja: 'AIとビジネス開発をマスター'
      },
      subtitle: {
        en: 'Learn from 21x GmbH experts and transform your career with cutting-edge knowledge',
        de: 'Lernen Sie von 21x GmbH-Experten und transformieren Sie Ihre Karriere mit modernsten Kenntnissen',
        fr: 'Apprenez des experts de 21x GmbH et transformez votre carrière avec des connaissances de pointe',
        es: 'Aprende de los expertos de 21x GmbH y transforma tu carrera con conocimientos de vanguardia',
        ja: '21x GmbHの専門家から学び、最先端の知識でキャリアを変革'
      }
    },
    {
      url: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8',
      title: {
        en: 'AI-Driven Business Solutions',
        de: 'KI-gesteuerte Geschäftslösungen',
        fr: 'Solutions commerciales pilotées par l\'IA',
        es: 'Soluciones empresariales impulsadas por IA',
        ja: 'AI主導のビジネスソリューション'
      },
      subtitle: {
        en: 'Practical courses in artificial intelligence, machine learning, and strategic business development',
        de: 'Praktische Kurse in künstlicher Intelligenz, maschinellem Lernen und strategischer Geschäftsentwicklung',
        fr: 'Cours pratiques en intelligence artificielle, apprentissage automatique et développement commercial stratégique',
        es: 'Cursos prácticos en inteligencia artificial, aprendizaje automático y desarrollo empresarial estratégico',
        ja: '人工知能、機械学習、戦略的ビジネス開発の実践的コース'
      }
    },
    {
      url: 'https://images.pexels.com/photos/5303547/pexels-photo-5303547.jpeg',
      title: {
        en: 'Expert-Led Consulting Training',
        de: 'Expertengeführte Beratungsausbildung',
        fr: 'Formation en conseil dirigée par des experts',
        es: 'Formación en consultoría dirigida por expertos',
        ja: '専門家主導のコンサルティング研修'
      },
      subtitle: {
        en: 'Learn from experienced 21x GmbH consultants and industry thought leaders',
        de: 'Lernen Sie von erfahrenen 21x GmbH-Beratern und Branchenvordenkern',
        fr: 'Apprenez des consultants expérimentés de 21x GmbH et des leaders d\'opinion de l\'industrie',
        es: 'Aprende de consultores experimentados de 21x GmbH y líderes de opinión de la industria',
        ja: '経験豊富な21x GmbHコンサルタントと業界のソートリーダーから学ぶ'
      }
    }
  ];

  return baseImages;
};

// Get attractive placeholder images for courses based on category
const getCoursePlaceholderImage = (category) => {
  const placeholders = {
    // Generic business thumbnails
    'Business Development': 'https://images.unsplash.com/photo-1628348068343-c6a848d2b6dd', // Charts trending up
    'Digital Transformation': 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab', // Modern office building
    'Business Consulting': 'https://images.unsplash.com/photo-1431540015161-0bf868a2d407', // Professional boardroom
    'Leadership': 'https://images.pexels.com/photos/416320/pexels-photo-416320.jpeg', // Meeting room hallway
    
    // AI and tech thumbnails
    'AI Analytics': 'https://images.unsplash.com/photo-1551288049-bebda4e38f71', // Data visualization
    'AI Strategy': 'https://images.unsplash.com/photo-1657644003083-a6f358244cb8', // Cute robot
    'Artificial Intelligence': 'https://images.unsplash.com/photo-1677442136019-21780ecad995', // AI technology
    
    // Legacy categories
    'Programming': 'https://images.unsplash.com/photo-1555949963-aa79dcee981c',
    'Data Science': 'https://images.unsplash.com/photo-1591696205602-2f950c417cb9', // Laptop with charts
    'Marketing': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f',
    'Technology': 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01', // Circuit brain
    'Business': 'https://images.unsplash.com/photo-1574958269340-fa927503f3dd', // Professional office
    'Design': 'https://images.unsplash.com/photo-1561070791-2526d30994b5',
    'default': 'https://images.pexels.com/photos/3184287/pexels-photo-3184287.jpeg' // Generic business chart
  };
  
  return placeholders[category] || placeholders['default'];
};

// Components
const RotatingBanner = () => {
  const { language, t } = useUser();
  const navigate = useNavigate();
  const [currentBanner, setCurrentBanner] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const banners = getBannerImages(language);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentBanner((prev) => (prev + 1) % banners.length);
        setIsTransitioning(false);
      }, 300); // Half of transition duration for smooth sync
    }, 5000); // Change banner every 5 seconds

    return () => clearInterval(interval);
  }, [banners.length]);

  const currentBannerData = banners[currentBanner];

  const nextBanner = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
      setIsTransitioning(false);
    }, 300);
  };

  const prevBanner = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
      setIsTransitioning(false);
    }, 300);
  };

  return (
    <section className="relative h-[600px] overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center transition-all duration-700 ease-in-out"
        style={{ backgroundImage: `url(${currentBannerData.url})` }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      </div>
      
      {/* Navigation Arrows */}
      <button
        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-black bg-opacity-20 hover:bg-opacity-40 text-white p-3 rounded-full transition-all hover:scale-110"
        onClick={nextBanner}
        aria-label="Previous banner"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      
      <button
        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-black bg-opacity-20 hover:bg-opacity-40 text-white p-3 rounded-full transition-all hover:scale-110"
        onClick={prevBanner}
        aria-label="Next banner"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      
      {/* Content */}
      <div className="relative z-10 flex items-center justify-center h-full text-white text-center px-4">
        <div className={`max-w-4xl mx-auto transition-opacity duration-700 ${isTransitioning ? 'opacity-30' : 'opacity-100'}`}>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in">
            {currentBannerData.title[language] || currentBannerData.title.en}
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            {currentBannerData.subtitle[language] || currentBannerData.subtitle.en}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="px-8 py-4 text-lg bg-blue-600 hover:bg-blue-700 transform hover:scale-105 transition-all"
              onClick={() => navigate('/courses')}
              data-testid="hero-browse-courses"
            >
              {t('browse_courses', 'Browse Courses')}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="px-8 py-4 text-lg border-white text-white hover:bg-white hover:text-black transform hover:scale-105 transition-all"
              onClick={() => navigate('/auth')}
              data-testid="hero-get-started"
            >
              {t('get_started_free', 'Get Started Free')}
            </Button>
          </div>
        </div>
      </div>

      {/* Enhanced Banner Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3">
        {banners.map((_, index) => (
          <button
            key={index}
            className={`w-4 h-4 rounded-full transition-all hover:scale-110 ${
              index === currentBanner 
                ? 'bg-white shadow-lg' 
                : 'bg-white bg-opacity-50 hover:bg-opacity-75'
            }`}
            onClick={() => setCurrentBanner(index)}
            aria-label={`Go to banner ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

const Navbar = () => {
  const { user, logout, language, setLanguage, t } = useUser();
  const navigate = useNavigate();

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'ja', name: '日本語' }
  ];

  return (
    <nav className="border-b bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center space-x-3 hover:opacity-90 transition-opacity"
              data-testid="logo-button"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_polyglot-learn-31/artifacts/x629raf6_Zeichenfla%CC%88che%201.png" 
                alt="21xacademy" 
                className="h-8 w-auto"
              />
              <span className="text-2xl font-bold text-blue-600">21xacademy</span>
            </button>
            
            {/* Navigation Links */}
            <div className="hidden md:flex ml-10 space-x-8">
              <button onClick={() => navigate('/courses')} className="text-gray-700 hover:text-blue-600 transition-colors">
                {t('courses', 'Courses')}
              </button>
              <button onClick={() => navigate('/about')} className="text-gray-700 hover:text-blue-600 transition-colors">
                {t('about', 'About')}
              </button>
              <button onClick={() => navigate('/how-it-works')} className="text-gray-700 hover:text-blue-600 transition-colors">
                {t('how_it_works', 'How It Works')}
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-32" data-testid="language-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map(lang => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {user ? (
              <div className="flex items-center space-x-4">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/my-courses')}
                  data-testid="my-courses-button"
                >
                  {t('my_courses', 'My Courses')}
                </Button>
                {user.is_admin && (
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/admin')}
                    data-testid="admin-button"
                  >
                    Admin
                  </Button>
                )}
                <div className="flex items-center space-x-2">
                  <Avatar>
                    <AvatarImage src={user.picture} />
                    <AvatarFallback>{user.name.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{user.name}</span>
                </div>
                <Button 
                  variant="outline" 
                  onClick={logout}
                  data-testid="logout-button"
                >
                  Logout
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/auth')}
                  data-testid="login-button"
                >
                  {t('login', 'Login')}
                </Button>
                <Button 
                  onClick={() => navigate('/auth')}
                  data-testid="register-button"
                >
                  {t('register', 'Register')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

const Home = () => {
  const { t, language } = useUser();
  const navigate = useNavigate();
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [stats, setStats] = useState({ courses: 0, students: 0, languages: 5 });

  useEffect(() => {
    loadFeaturedCourses();
    loadStats();
  }, [language]);

  const loadFeaturedCourses = async () => {
    try {
      const response = await api.get(`/courses?language=${language}`);
      setFeaturedCourses(response.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to load courses:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/courses');
      setStats(prev => ({ ...prev, courses: response.data.length }));
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Rotating Banner */}
      <RotatingBanner />

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">{stats.courses}+</div>
              <div className="text-xl opacity-90">{t('courses_available', 'Courses Available')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">10k+</div>
              <div className="text-xl opacity-90">{t('active_students', 'Active Students')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">{stats.languages}</div>
              <div className="text-xl opacity-90">{t('languages_supported', 'Languages Supported')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">
            {t('why_choose_learnstream', 'Why Choose LearnStream?')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Multilingual Card */}
            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 opacity-50"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200 to-blue-300 rounded-full transform translate-x-16 -translate-y-16 opacity-30 pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue-300 to-blue-400 rounded-full transform -translate-x-12 translate-y-12 opacity-20 pointer-events-none"></div>
              <CardHeader className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <CardTitle className="text-2xl mb-4 text-gray-800">{t('multilingual', 'Multilingual')}</CardTitle>
              </CardHeader>
              <CardContent className="relative z-10">
                <CardDescription className="text-lg text-gray-600">
                  {t('multilingual_desc', 'Courses available in 5+ languages with AI-powered translations for global accessibility.')}
                </CardDescription>
              </CardContent>
            </Card>

            {/* Self-Paced Card */}
            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-green-100 opacity-50"></div>
              <div className="absolute top-0 left-0 w-28 h-28 bg-gradient-to-br from-green-200 to-green-300 rounded-full transform -translate-x-14 -translate-y-14 opacity-30 pointer-events-none"></div>
              <div className="absolute bottom-0 right-0 w-36 h-36 bg-gradient-to-tl from-green-300 to-green-400 rounded-full transform translate-x-18 translate-y-18 opacity-20 pointer-events-none"></div>
              <CardHeader className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <CardTitle className="text-2xl mb-4 text-gray-800">{t('self_paced', 'Self-Paced')}</CardTitle>
              </CardHeader>
              <CardContent className="relative z-10">
                <CardDescription className="text-lg text-gray-600">
                  {t('self_paced_desc', 'Learn at your own speed with lifetime access to all course materials and videos.')}
                </CardDescription>
              </CardContent>
            </Card>

            {/* Expert Instructors Card */}
            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-purple-100 opacity-50"></div>
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-purple-200 to-purple-300 rounded-full transform translate-x-20 -translate-y-20 opacity-25 pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-purple-400 to-purple-500 rounded-full transform -translate-x-10 translate-y-10 opacity-30 pointer-events-none"></div>
              <CardHeader className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <CardTitle className="text-2xl mb-4 text-gray-800">{t('expert_instructors', 'Expert Instructors')}</CardTitle>
              </CardHeader>
              <CardContent className="relative z-10">
                <CardDescription className="text-lg text-gray-600">
                  {t('expert_instructors_desc', 'Learn from industry professionals and certified educators with real-world experience.')}
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      {featuredCourses.length > 0 && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">
              {t('featured_courses', 'Featured Courses')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredCourses.map((course) => (
                <Card key={course.id} className="hover:shadow-xl transition-shadow duration-300 border-0 shadow-lg">
                  <CardHeader>
                    <div className="aspect-video bg-gray-200 rounded-lg mb-4 overflow-hidden">
                      <img 
                        src={course.thumbnail_url || getCoursePlaceholderImage(course.category)} 
                        alt={course.title} 
                        className="w-full h-full object-cover" 
                        loading="lazy"
                      />
                    </div>
                    <div className="flex justify-between items-start mb-2">
                      <Badge variant="secondary" className="text-xs">{course.category}</Badge>
                      <Badge className="text-xs">{t(course.difficulty_level, course.difficulty_level)}</Badge>
                    </div>
                    <CardTitle className="line-clamp-2 text-xl">{course.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="line-clamp-3 mb-4 text-gray-600">
                      {course.short_description}
                    </CardDescription>
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-3xl font-bold text-green-600">${course.price}</span>
                      <span className="text-sm text-gray-500">{course.video_count} videos</span>
                    </div>
                  </CardContent>
                  <CardFooter className="flex flex-col space-y-2">
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3"
                      onClick={() => navigate(`/checkout/${course.id}`)}
                    >
                      💳 {t('buy_now', 'Buy Now')} - ${course.price}
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate(`/courses/${course.id}`)}
                    >
                      👁️ {t('view_course', 'View Course')}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
            <div className="text-center mt-12">
              <Button 
                size="lg" 
                onClick={() => navigate('/courses')}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {t('view_all_courses', 'View All Courses')}
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">
            {t('start_learning_today', 'Start Learning Today')}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {t('join_thousands', 'Join thousands of students already learning on our platform')}
          </p>
          <Button 
            size="lg" 
            className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-3 text-lg font-semibold transform hover:scale-105 transition-all"
            onClick={() => navigate('/auth')}
          >
            {t('get_started_free', 'Get Started Free')}
          </Button>
        </div>
      </section>
    </div>
  );
};

const AboutUs = () => {
  const { t } = useUser();
  
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-6">{t('about_us', 'About Us')}</h1>
          <p className="text-xl opacity-90">
            {t('about_subtitle', 'Empowering learners worldwide through multilingual education')}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">{t('our_mission', 'Our Mission')}</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            {t('mission_text', 'At LearnStream, we believe that language should never be a barrier to learning. Our mission is to democratize education by providing high-quality courses in multiple languages, powered by cutting-edge AI translation technology.')}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            {t('vision_text', 'We envision a world where anyone, anywhere, can access world-class education in their native language and learn new skills to advance their career and personal growth.')}
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-16">{t('our_values', 'Our Values')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl">{t('accessibility', 'Accessibility')}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600">
                  {t('accessibility_desc', 'Making quality education accessible to everyone, regardless of language or location.')}
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card className="text-center border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl">{t('quality', 'Quality')}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600">
                  {t('quality_desc', 'Maintaining the highest standards in course content and learning experience.')}
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card className="text-center border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl">{t('innovation', 'Innovation')}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600">
                  {t('innovation_desc', 'Leveraging the latest technology to create better learning experiences.')}
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

const HowItWorks = () => {
  const { t } = useUser();
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-6">{t('how_it_works', 'How It Works')}</h1>
          <p className="text-xl opacity-90">
            {t('how_it_works_subtitle', 'Learn how to get started and make the most of your learning journey')}
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-16">{t('getting_started', 'Getting Started')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">{t('step_1_title', 'Create Account')}</h3>
              <p className="text-gray-600">
                {t('step_1_desc', 'Sign up for free and set your preferred language for the best learning experience.')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">{t('step_2_title', 'Browse Courses')}</h3>
              <p className="text-gray-600">
                {t('step_2_desc', 'Explore our vast library of courses available in your preferred language.')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">{t('step_3_title', 'Start Learning')}</h3>
              <p className="text-gray-600">
                {t('step_3_desc', 'Enroll in courses and learn at your own pace with lifetime access.')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">{t('platform_features', 'Platform Features')}</h2>
          <div className="space-y-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold">{t('multilingual_content', 'Multilingual Content')}</h3>
                <p className="text-gray-600">{t('multilingual_content_desc', 'All courses are available in multiple languages with AI-powered translations.')}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold">{t('progress_tracking', 'Progress Tracking')}</h3>
                <p className="text-gray-600">{t('progress_tracking_desc', 'Track your learning progress and pick up where you left off.')}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold">{t('lifetime_access', 'Lifetime Access')}</h3>
                <p className="text-gray-600">{t('lifetime_access_desc', 'Once you enroll, you have lifetime access to all course materials.')}</p>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Button 
              size="lg" 
              onClick={() => navigate('/courses')}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {t('start_browsing', 'Start Browsing Courses')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

// Keep existing components (Auth, Courses, CourseDetail, MyCourses, Admin, PaymentSuccess) unchanged
const Auth = () => {
  const { login, t } = useUser();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await api.post(endpoint, formData);
      
      login(response.data.user, response.data.access_token);
      toast.success(isLogin ? 'Logged in successfully!' : 'Account created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-extrabold">
              {isLogin ? t('login', 'Login') : t('register', 'Register')}
            </CardTitle>
            <CardDescription>
              {isLogin ? t('sign_in_account', 'Sign in to your account') : t('create_new_account', 'Create your new account')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Social Login Buttons */}
            <div className="space-y-3 mb-6">
              <Button
                type="button"
                variant="outline"
                className="w-full flex items-center justify-center gap-3 py-3"
                onClick={() => {
                  const redirectUrl = encodeURIComponent(`${window.location.origin}/`);
                  const authUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
                  window.location.href = `${authUrl}/?redirect=${redirectUrl}`;
                }}
                data-testid="google-login-button"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('continue_with_google', 'Continue with Google')}
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-50 text-gray-500">{t('or', 'or')}</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
              {!isLogin && (
                <div>
                  <Label htmlFor="name">{t('full_name', 'Full Name')}</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required={!isLogin}
                    data-testid="name-input"
                    placeholder={t('enter_full_name', 'Enter your full name')}
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="email">{t('email', 'Email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="email-input"
                  placeholder={t('enter_email', 'Enter your email address')}
                />
              </div>

              <div>
                <Label htmlFor="password">{t('password', 'Password')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  data-testid="password-input"
                  placeholder={t('enter_password', 'Enter your password')}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? t('loading', 'Loading...') : (isLogin ? t('login', 'Login') : t('register', 'Register'))}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="text-center">
            <Button 
              variant="link" 
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-button"
            >
              {isLogin ? t('no_account_register', "Don't have an account? Register") : t('have_account_login', "Already have an account? Login")}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

const Courses = () => {
  const { user, language, t } = useUser();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    loadCourses();
  }, [language]);

  const loadCourses = async () => {
    try {
      const response = await api.get(`/courses?language=${language}`);
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to load courses');
    }
    setLoading(false);
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || course.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(courses.map(course => course.category))];

  const handleDirectPurchase = async (course) => {
    setLoading(true);
    try {
      const response = await api.post('/payments/create-session', {
        course_id: course.id,
        payment_method: 'stripe'
      });
      
      // Redirect to payment
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to initiate purchase');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">{t('loading_courses', 'Loading courses...')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4" data-testid="courses-title">
          {t('courses', 'Courses')}
        </h1>
        
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Input
            placeholder={t('search_courses', 'Search courses...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
            data-testid="search-input"
          />
          
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="max-w-xs" data-testid="category-filter">
              <SelectValue placeholder={t('category', 'Category')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_categories', 'All Categories')}</SelectItem>
              {categories.map(category => (
                <SelectItem key={category} value={category}>
                  {t(`category_${category.toLowerCase().replace(' ', '_')}`, category)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredCourses.map((course) => (
          <Card key={course.id} className="hover:shadow-lg transition-shadow" data-testid={`course-card-${course.id}`}>
            <CardHeader>
              <div className="aspect-video bg-gray-200 rounded-lg mb-4">
                <img 
                  src={course.thumbnail_url || getCoursePlaceholderImage(course.category)} 
                  alt={course.title} 
                  className="w-full h-full object-cover rounded-lg" 
                />
              </div>
              <div className="flex justify-between items-start mb-2">
                <Badge variant="secondary">{course.category}</Badge>
                <Badge>{t(course.difficulty_level, course.difficulty_level)}</Badge>
              </div>
              <CardTitle className="line-clamp-2">{course.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="line-clamp-3 mb-4">
                {course.short_description}
              </CardDescription>
              <div className="flex justify-between items-center">
                <span className="text-2xl font-bold text-green-600">${course.price}</span>
                <span className="text-sm text-gray-500">{course.video_count} videos</span>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-2">
              <Button 
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3"
                onClick={() => navigate(`/checkout/${course.id}`)}
                data-testid={`buy-now-${course.id}`}
              >
                💳 {t('buy_now', 'Buy Now')} - ${course.price}
              </Button>
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => navigate(`/courses/${course.id}`)}
                data-testid={`view-course-${course.id}`}
              >
                👁️ View Course Details
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>

      {filteredCourses.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">{t('no_courses_found', 'No courses found matching your criteria.')}</p>
        </div>
      )}
    </div>
  );
};

const CourseDetail = () => {
  const { user, language, t } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPayment(sessionId);
    }
    
    // Check for buy-now anchor
    if (window.location.hash === '#buy-now') {
      setTimeout(() => {
        document.getElementById('enrollment-card')?.scrollIntoView({ behavior: 'smooth' });
      }, 1000);
    }
  }, [courseId, language]);

  const loadCourse = async () => {
    try {
      const response = await api.get(`/courses/${courseId}?language=${language}`);
      setCourse(response.data);
    } catch (error) {
      toast.error('Failed to load course');
      navigate('/courses');
    }
    setLoading(false);
  };

  const verifyPayment = async (sessionId) => {
    try {
      const response = await api.get(`/payments/verify/${sessionId}`);
      toast.success(response.data.message);
      loadCourse(); // Reload to show enrollment status
    } catch (error) {
      toast.error('Payment verification failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading course...</div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Course not found.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Course Info */}
        <div className="lg:col-span-2">
          <div className="aspect-video bg-gray-200 rounded-lg mb-6">
            <img 
              src={course.thumbnail_url || getCoursePlaceholderImage(course.category)} 
              alt={course.title} 
              className="w-full h-full object-cover rounded-lg" 
            />
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-4 mb-4">
              <Badge variant="secondary">{course.category}</Badge>
              <Badge>{t(course.difficulty_level, course.difficulty_level)}</Badge>
            </div>
            <h1 className="text-3xl font-bold mb-4" data-testid="course-title">{course.title}</h1>
            <p className="text-lg text-gray-600 mb-6">{course.description}</p>
            
            {/* Authors Section */}
            {course.authors && course.authors.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">
                  {course.authors.length === 1 ? 'Instructor' : 'Instructors'}
                </h3>
                <div className="space-y-4">
                  {course.authors.map((author) => (
                    <div key={author.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                        {author.profile_image_url ? (
                          <img 
                            src={author.profile_image_url} 
                            alt={author.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-2xl font-bold text-gray-500">
                            {author.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">{author.name}</h4>
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">{author.bio}</p>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {author.expertise?.slice(0, 4).map((skill, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          {author.years_experience && (
                            <span>{author.years_experience} years experience</span>
                          )}
                          <span>{author.total_courses || 0} courses</span>
                          <span>{author.total_students || 0} students</span>
                          {author.is_verified && (
                            <Badge variant="secondary" className="text-xs">
                              ✓ Verified
                            </Badge>
                          )}
                        </div>
                        {/* Social Links */}
                        {author.social_links && Object.keys(author.social_links).length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {author.social_links.linkedin && (
                              <a
                                href={author.social_links.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-xs"
                              >
                                LinkedIn
                              </a>
                            )}
                            {author.social_links.twitter && (
                              <a
                                href={author.social_links.twitter}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-600 text-xs"
                              >
                                Twitter
                              </a>
                            )}
                            {author.social_links.website && (
                              <a
                                href={author.social_links.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-600 hover:text-gray-800 text-xs"
                              >
                                Website
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Course Content with Chapters */}
          {course.videos && course.videos.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>📚 Course Content</CardTitle>
                <CardDescription>{course.videos.length} videos organized in chapters</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Chapter 1: First 5 videos */}
                  <div>
                    <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
                      {t('chapter', 'Chapter')} 1: Fundamentals
                    </h4>
                    <div className="space-y-3">
                      {course.videos.slice(0, 5).map((video, index) => (
                        <div key={video.id} className="flex items-center gap-4 p-3 border rounded-lg">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <h5 className="font-medium">{video.title}</h5>
                            {video.description && (
                              <p className="text-sm text-gray-600">{video.description}</p>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {Math.floor(video.duration_seconds / 60)}m {video.duration_seconds % 60}s
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Chapter 2: Remaining videos if more than 5 */}
                  {course.videos.length > 5 && (
                    <div>
                      <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
                        {t('chapter', 'Chapter')} 2: Advanced Concepts
                      </h4>
                      <div className="space-y-3">
                        {course.videos.slice(5).map((video, index) => (
                          <div key={video.id} className="flex items-center gap-4 p-3 border rounded-lg">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
                              {index + 6}
                            </div>
                            <div className="flex-1">
                              <h5 className="font-medium">{video.title}</h5>
                              {video.description && (
                                <p className="text-sm text-gray-600">{video.description}</p>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {Math.floor(video.duration_seconds / 60)}m {video.duration_seconds % 60}s
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Enhanced Enrollment Card */}
        <div className="lg:col-span-1">
          <Card className="sticky top-8" id="enrollment-card">
            <CardHeader>
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">${course.price}</div>
                <div className="text-sm text-gray-500">One-time payment • Lifetime access</div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Course Benefits */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-2">✨ What you'll get:</h4>
                <div className="space-y-2 text-sm text-green-700">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{course.video_count} expert-led videos</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Lifetime access to all materials</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>21x GmbH expertise and insights</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Certificate of completion</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>30-day money-back guarantee</span>
                  </div>
                </div>
              </div>
              
              {/* Course Stats */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="font-semibold">{course.video_count}</div>
                  <div className="text-gray-600">Videos</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="font-semibold">{Math.floor(course.duration_minutes / 60)}h {course.duration_minutes % 60}m</div>
                  <div className="text-gray-600">Duration</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="font-semibold capitalize">{course.difficulty_level}</div>
                  <div className="text-gray-600">Level</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="font-semibold">∞</div>
                  <div className="text-gray-600">Access</div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="space-y-3">
              <Button 
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 text-lg" 
                size="lg"
                onClick={() => navigate(`/checkout/${courseId}`)}
                disabled={enrolling}
                data-testid="enroll-button"
              >
                <div className="flex items-center justify-center gap-2">
                  💳 {t('buy_now', 'Buy Now')} - ${course.price}
                </div>
              </Button>
              
              <div className="text-center text-xs text-gray-500">
                <p>🔒 Secure payment • 30-day guarantee • Instant access</p>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

const MyCourses = () => {
  const { user, language, t } = useUser();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadMyCourses();
    } else {
      navigate('/auth');
    }
  }, [user, language]);

  const loadMyCourses = async () => {
    try {
      const response = await api.get(`/my-courses?language=${language}`);
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to load your courses');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading your courses...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-8" data-testid="my-courses-title">
        {t('my_courses', 'My Courses')}
      </h1>

      {courses.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <p className="text-gray-500 text-lg mb-4">{t('no_courses_enrolled', 'You haven\'t enrolled in any courses yet.')}</p>
            <Button 
              onClick={() => navigate('/courses')} 
              data-testid="browse-courses-cta"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {t('browse_courses', 'Browse Courses')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {courses.map((course) => (
            <Card key={course.id} className="hover:shadow-lg transition-shadow" data-testid={`my-course-${course.id}`}>
              <CardHeader>
                <div className="aspect-video bg-gray-200 rounded-lg mb-4">
                  <img 
                    src={course.thumbnail_url || getCoursePlaceholderImage(course.category)} 
                    alt={course.title} 
                    className="w-full h-full object-cover rounded-lg" 
                  />
                </div>
                <Badge variant="secondary" className="w-fit">{course.category}</Badge>
                <CardTitle className="line-clamp-2">{course.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="line-clamp-3 mb-4">
                  {course.short_description}
                </CardDescription>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progress:</span>
                    <span>{Math.round(course.progress_percentage)}%</span>
                  </div>
                  <Progress value={course.progress_percentage} className="h-2" />
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => navigate(`/courses/${course.id}`)}
                >
                  {t('continue_learning', 'Continue Learning')}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const Admin = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);

  // Course form state
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    short_description: '',
    category: '',
    difficulty_level: 'beginner',
    price: '',
    author_ids: [],
    thumbnail_url: ''
  });

  // Video form state  
  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    video_url: '',
    duration_seconds: ''
  });

  // CMS states
  const [banners, setBanners] = useState([]);
  const [features, setFeatures] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [authors, setAuthors] = useState([]);
  const [legalPages, setLegalPages] = useState([]);
  const [editingCourse, setEditingCourse] = useState(null);

  const [selectedCourseId, setSelectedCourseId] = useState('');

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }
    loadAdminData();
  }, [user]);

  const loadAdminData = async () => {
    try {
      const [coursesRes, bannersRes, featuresRes, newslettersRes, subscribersRes, settingsRes, authorsRes, legalRes] = await Promise.all([
        api.get('/admin/courses'), // Get all courses for admin management
        api.get('/cms/banners'),
        api.get('/cms/features'),
        api.get('/admin/newsletters'),
        api.get('/admin/subscribers'),
        api.get('/cms/settings'),
        api.get('/authors'),
        api.get('/legal')
      ]);
      
      setCourses(coursesRes.data);
      setBanners(bannersRes.data);
      setFeatures(featuresRes.data);
      setNewsletters(newslettersRes.data);
      setSubscribers(subscribersRes.data);
      setSiteSettings(settingsRes.data);
      setAuthors(authorsRes.data);
      setLegalPages(legalRes.data);
    } catch (error) {
      console.error('Failed to load admin data:', error);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingCourse) {
        // Update existing course
        await api.put(`/admin/courses/${editingCourse.id}`, {
          ...courseForm,
          price: parseFloat(courseForm.price)
        });
        toast.success('Course updated successfully!');
        setEditingCourse(null);
      } else {
        // Create new course
        await api.post('/admin/courses', {
          ...courseForm,
          price: parseFloat(courseForm.price)
        });
        toast.success('Course created successfully!');
      }
      
      setCourseForm({
        title: '',
        description: '',
        short_description: '',
        category: '',
        difficulty_level: 'beginner',
        price: '',
        author_ids: [],
        thumbnail_url: ''
      });
      loadAdminData();
    } catch (error) {
      toast.error(editingCourse ? 'Failed to update course' : 'Failed to create course');
    }
    setLoading(false);
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    if (!selectedCourseId) {
      toast.error('Please select a course first');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/admin/courses/${selectedCourseId}/videos`, {
        ...videoForm,
        duration_seconds: parseInt(videoForm.duration_seconds)
      });
      
      toast.success('Video added successfully!');
      setVideoForm({
        title: '',
        description: '',
        video_url: '',
        duration_seconds: ''
      });
    } catch (error) {
      toast.error('Failed to add video');
    }
    setLoading(false);
  };

  const handlePublishCourse = async (courseId) => {
    try {
      await api.put(`/admin/courses/${courseId}/publish`);
      toast.success('Course published successfully!');
      loadAdminData();
    } catch (error) {
      toast.error('Failed to publish course');
    }
  };

  const handleToggleVisibility = async (courseId, isVisible) => {
    try {
      await api.put(`/admin/courses/${courseId}/visibility?is_visible=${!isVisible}`);
      toast.success(`Course ${!isVisible ? 'made visible' : 'hidden'} successfully!`);
      loadAdminData();
    } catch (error) {
      toast.error('Failed to toggle course visibility');
    }
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setCourseForm({
      title: course.title || '',
      description: course.description || '',
      short_description: course.short_description || '',
      category: course.category || '',
      difficulty_level: course.difficulty_level || 'beginner',
      price: course.price?.toString() || '',
      author_ids: course.author_ids || [],
      thumbnail_url: course.thumbnail_url || ''
    });
    setActiveTab('create-course'); // Switch to create-course tab for editing
  };

  const handleImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/admin/upload/course-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setCourseForm({
        ...courseForm,
        thumbnail_url: response.data.image_url
      });
      
      toast.success('Image uploaded successfully!');
      return response.data.image_url;
    } catch (error) {
      toast.error('Failed to upload image');
      return null;
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      try {
        await api.delete(`/admin/courses/${courseId}`);
        toast.success('Course deleted successfully!');
        loadAdminData();
      } catch (error) {
        toast.error('Failed to delete course');
      }
    }
  };

  const handleImportVideos = async (courseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post(`/admin/courses/${courseId}/import-videos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(`${response.data.imported_count} videos imported successfully!`);
      if (response.data.errors.length > 0) {
        console.warn('Import errors:', response.data.errors);
      }
      loadAdminData();
    } catch (error) {
      toast.error('Failed to import videos');
    }
  };

  const updateSiteSettings = async (newSettings) => {
    try {
      await api.put('/admin/cms/settings', newSettings);
      setSiteSettings(prev => ({ ...prev, ...newSettings }));
      toast.success('Settings updated successfully!');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (!user || !user.is_admin) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-8" data-testid="admin-title">Admin Dashboard</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-8 grid grid-cols-11 w-full">
          <TabsTrigger value="courses" data-testid="courses-tab">Courses</TabsTrigger>
          <TabsTrigger value="create-course" data-testid="create-course-tab">Create Course</TabsTrigger>
          <TabsTrigger value="manage-videos" data-testid="manage-videos-tab">Manage Videos</TabsTrigger>
          <TabsTrigger value="add-video" data-testid="add-video-tab">Add Video</TabsTrigger>
          <TabsTrigger value="authors" data-testid="authors-tab">Authors</TabsTrigger>
          <TabsTrigger value="banners" data-testid="banners-tab">Banners</TabsTrigger>
          <TabsTrigger value="features" data-testid="features-tab">Features</TabsTrigger>
          <TabsTrigger value="pages" data-testid="pages-tab">Pages</TabsTrigger>
          <TabsTrigger value="legal" data-testid="legal-tab">Legal</TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="newsletter-tab">Newsletter</TabsTrigger>
          <TabsTrigger value="settings" data-testid="settings-tab">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="courses">
          <Card>
            <CardHeader>
              <CardTitle>Course Management</CardTitle>
              <CardDescription>Manage your courses with full control over publishing and visibility</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {courses.map((course) => (
                  <div key={course.id} className="p-4 border rounded-lg" data-testid={`admin-course-${course.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-lg">{course.title || 'Untitled Course'}</h3>
                        <p className="text-sm text-gray-600 mb-2">{course.category} • ${course.price} • {course.video_count} videos</p>
                        {course.authors && course.authors.length > 0 && (
                          <p className="text-xs text-gray-500 mb-2">
                            Authors: {course.authors.map(a => a.name).join(', ')}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <Badge variant={course.is_published ? "default" : "secondary"}>
                            {course.is_published ? "📢 Published" : "📝 Draft"}
                          </Badge>
                          <Badge variant={course.is_visible ? "default" : "outline"}>
                            {course.is_visible ? "👁️ Visible" : "🙈 Hidden"}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2 ml-4">
                        {/* Visibility Toggle */}
                        <div className="flex items-center space-x-2">
                          <span className="text-xs">{course.is_visible ? 'Visible' : 'Hidden'}</span>
                          <button
                            onClick={() => handleToggleVisibility(course.id, course.is_visible)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              course.is_visible ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                course.is_visible ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        
                        <div className="flex gap-1">
                          {!course.is_published && (
                            <Button 
                              size="sm"
                              onClick={() => handlePublishCourse(course.id)}
                              data-testid={`publish-course-${course.id}`}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              Publish
                            </Button>
                          )}
                          
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditCourse(course)}
                            data-testid={`edit-course-${course.id}`}
                          >
                            Edit
                          </Button>
                          
                          <Button 
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteCourse(course.id)}
                            data-testid={`delete-course-${course.id}`}
                          >
                            Delete
                          </Button>
                          
                          {/* CSV Import Button */}
                          <label className="cursor-pointer">
                            <Button size="sm" variant="secondary" asChild>
                              <span>Import CSV</span>
                            </Button>
                            <input
                              type="file"
                              accept=".csv"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleImportVideos(course.id, e.target.files[0]);
                                }
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="create-course">
          <Card>
            <CardHeader>
              <CardTitle>{editingCourse ? 'Edit Course' : 'Create New Course'}</CardTitle>
              <CardDescription>
                {editingCourse ? 'Update course details with automatic multilingual translations' : 'Add a new course with automatic multilingual translations'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {editingCourse && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Editing:</strong> {editingCourse.title}
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setEditingCourse(null);
                      setCourseForm({
                        title: '',
                        description: '',
                        short_description: '',
                        category: '',
                        difficulty_level: 'beginner',
                        price: '',
                        author_ids: [],
                        thumbnail_url: ''
                      });
                    }}
                    className="mt-2"
                  >
                    Cancel Edit
                  </Button>
                </div>
              )}
              
              <form onSubmit={handleCreateCourse} className="space-y-4" data-testid="create-course-form">
                <div>
                  <Label htmlFor="title">Course Title</Label>
                  <Input
                    id="title"
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({...courseForm, title: e.target.value})}
                    required
                    data-testid="course-title-input"
                  />
                </div>

                <div>
                  <Label htmlFor="short_description">Short Description</Label>
                  <Input
                    id="short_description"
                    value={courseForm.short_description}
                    onChange={(e) => setCourseForm({...courseForm, short_description: e.target.value})}
                    required
                    data-testid="course-short-description-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Full Description</Label>
                  <Textarea
                    id="description"
                    value={courseForm.description}
                    onChange={(e) => setCourseForm({...courseForm, description: e.target.value})}
                    required
                    rows={4}
                    data-testid="course-description-input"
                  />
                </div>

                {/* Course Image Upload */}
                <div>
                  <Label>Course Image</Label>
                  <div className="space-y-4">
                    {/* Image Preview */}
                    {courseForm.thumbnail_url && (
                      <div className="flex items-center gap-4">
                        <img 
                          src={courseForm.thumbnail_url} 
                          alt="Course thumbnail preview" 
                          className="w-32 h-20 object-cover rounded-lg border"
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setCourseForm({...courseForm, thumbnail_url: ''})}
                        >
                          Remove Image
                        </Button>
                      </div>
                    )}
                    
                    {/* Image Upload */}
                    <div>
                      <label className="block">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer">
                          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <p className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Click to upload course image</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">PNG, JPG, JPEG, WEBP up to 10MB</p>
                        </div>
                        <input
                          type="file"
                          accept="image/png,image/jpg,image/jpeg,image/webp"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleImageUpload(e.target.files[0]);
                            }
                          }}
                        />
                      </label>
                    </div>
                    
                    {/* Manual URL Input */}
                    <div>
                      <Label htmlFor="thumbnail_url">Or enter image URL</Label>
                      <Input
                        id="thumbnail_url"
                        value={courseForm.thumbnail_url}
                        onChange={(e) => setCourseForm({...courseForm, thumbnail_url: e.target.value})}
                        placeholder="https://example.com/course-image.jpg"
                        data-testid="course-thumbnail-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Input
                      id="category"
                      value={courseForm.category}
                      onChange={(e) => setCourseForm({...courseForm, category: e.target.value})}
                      required
                      data-testid="course-category-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="price">Price ($)</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={courseForm.price}
                      onChange={(e) => setCourseForm({...courseForm, price: e.target.value})}
                      required
                      data-testid="course-price-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="difficulty">Difficulty Level</Label>
                  <Select 
                    value={courseForm.difficulty_level} 
                    onValueChange={(value) => setCourseForm({...courseForm, difficulty_level: value})}
                  >
                    <SelectTrigger data-testid="difficulty-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="authors">Course Authors</Label>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Select one or more authors for this course</p>
                    <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto border rounded p-2">
                      {authors.map((author) => (
                        <label key={author.id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={courseForm.author_ids.includes(author.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCourseForm({
                                  ...courseForm,
                                  author_ids: [...courseForm.author_ids, author.id]
                                });
                              } else {
                                setCourseForm({
                                  ...courseForm,
                                  author_ids: courseForm.author_ids.filter(id => id !== author.id)
                                });
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">{author.name}</span>
                          <span className="text-xs text-gray-500">
                            ({author.expertise?.slice(0, 2).join(', ')})
                          </span>
                        </label>
                      ))}
                    </div>
                    {courseForm.author_ids.length === 0 && (
                      <p className="text-xs text-gray-500">Note: If no authors are selected, you will be set as the default author</p>
                    )}
                  </div>
                </div>

                <Button type="submit" disabled={loading} data-testid="create-course-submit">
                  {loading ? (editingCourse ? 'Updating...' : 'Creating...') : (editingCourse ? 'Update Course' : 'Create Course')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="manage-videos">
          <VideoManagement courses={courses} onUpdate={loadAdminData} />
        </TabsContent>

        <TabsContent value="add-video">
          <div className="space-y-6">
            {/* Single Video Addition */}
            <Card>
              <CardHeader>
                <CardTitle>Add Single Video</CardTitle>
                <CardDescription>Add individual videos with automatic multilingual metadata</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddVideo} className="space-y-4" data-testid="add-video-form">
                  <div>
                    <Label htmlFor="course">Select Course</Label>
                    <Select value={selectedCourseId} onValueChange={setSelectedCourseId}>
                      <SelectTrigger data-testid="course-select">
                        <SelectValue placeholder="Choose a course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map(course => (
                          <SelectItem key={course.id} value={course.id}>
                            {course.title || 'Untitled Course'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="video_title">Video Title</Label>
                    <Input
                      id="video_title"
                      value={videoForm.title}
                      onChange={(e) => setVideoForm({...videoForm, title: e.target.value})}
                      required
                      data-testid="video-title-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="video_description">Video Description</Label>
                    <Textarea
                      id="video_description"
                      value={videoForm.description}
                      onChange={(e) => setVideoForm({...videoForm, description: e.target.value})}
                      rows={3}
                      data-testid="video-description-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="video_url">Video URL</Label>
                    <Input
                      id="video_url"
                      type="url"
                      value={videoForm.video_url}
                      onChange={(e) => setVideoForm({...videoForm, video_url: e.target.value})}
                      required
                      placeholder="https://example.com/video.mp4"
                      data-testid="video-url-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="duration">Duration (seconds)</Label>
                    <Input
                      id="duration"
                      type="number"
                      value={videoForm.duration_seconds}
                      onChange={(e) => setVideoForm({...videoForm, duration_seconds: e.target.value})}
                      required
                      data-testid="video-duration-input"
                    />
                  </div>

                  <Button type="submit" disabled={loading} data-testid="add-video-submit">
                    {loading ? 'Adding...' : 'Add Video'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* CSV Import Section */}
            <Card>
              <CardHeader>
                <CardTitle>📥 Bulk CSV Import</CardTitle>
                <CardDescription>Import multiple videos at once using CSV file</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Course Selection for CSV Import */}
                  <div>
                    <Label>Select Course for CSV Import</Label>
                    <Select value={selectedCourseId} onValueChange={setSelectedCourseId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map(course => (
                          <SelectItem key={course.id} value={course.id}>
                            {course.title || 'Untitled Course'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* CSV Format Info */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">📋 CSV Format Requirements</h4>
                    <p className="text-sm text-blue-700 mb-3">Your CSV file should contain the following columns:</p>
                    <div className="bg-white p-3 rounded border text-xs font-mono">
                      <strong>Required columns:</strong> title, video_url, duration_seconds<br/>
                      <strong>Optional columns:</strong> description, order_index, thumbnail_url
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      ⬇️ <a href="/sample_video_import.csv" download className="underline">Download sample CSV template</a>
                    </p>
                  </div>

                  {/* CSV File Upload */}
                  <div>
                    <Label>Upload CSV File</Label>
                    <div className="mt-2">
                      <label className="block">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer">
                          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <p className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">CSV files only</p>
                        </div>
                        <input
                          type="file"
                          accept=".csv"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0] && selectedCourseId) {
                              handleImportVideos(selectedCourseId, e.target.files[0]);
                            } else if (!selectedCourseId) {
                              toast.error('Please select a course first');
                            }
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="authors">
          <AuthorManagement authors={authors} onUpdate={loadAdminData} />
        </TabsContent>

        <TabsContent value="banners">
          <BannerManagement banners={banners} onUpdate={loadAdminData} />
        </TabsContent>

        <TabsContent value="features">
          <FeatureManagement features={features} onUpdate={loadAdminData} />
        </TabsContent>

        <TabsContent value="pages">
          <PageContentManagement onUpdate={loadAdminData} />
        </TabsContent>

        <TabsContent value="legal">
          <LegalPageManagement legalPages={legalPages} onUpdate={loadAdminData} />
        </TabsContent>

        <TabsContent value="newsletter">
          <NewsletterManagement 
            newsletters={newsletters} 
            subscribers={subscribers}
            onUpdate={loadAdminData} 
          />
        </TabsContent>

        <TabsContent value="settings">
          <SiteSettingsManagement 
            settings={siteSettings} 
            onUpdate={updateSiteSettings} 
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Banner Management Component
const BannerManagement = ({ banners, onUpdate }) => {
  const [editingBanner, setEditingBanner] = useState(null);
  const [newBanner, setNewBanner] = useState({
    banner_index: 0,
    image_url: '',
    translations: {
      en: { title: '', subtitle: '', button_text: 'Browse Courses' },
      de: { title: '', subtitle: '', button_text: 'Kurse durchsuchen' },
      fr: { title: '', subtitle: '', button_text: 'Parcourir les cours' },
      es: { title: '', subtitle: '', button_text: 'Explorar cursos' },
      ja: { title: '', subtitle: '', button_text: 'コースを閲覧' }
    }
  });

  const handleSaveBanner = async () => {
    try {
      if (editingBanner) {
        await api.put(`/admin/cms/banners/${editingBanner.id}`, newBanner);
        toast.success('Banner updated successfully!');
      } else {
        await api.post('/admin/cms/banners', newBanner);
        toast.success('Banner created successfully!');
      }
      setEditingBanner(null);
      setNewBanner({
        banner_index: 0,
        image_url: '',
        translations: {
          en: { title: '', subtitle: '', button_text: 'Browse Courses' },
          de: { title: '', subtitle: '', button_text: 'Kurse durchsuchen' },
          fr: { title: '', subtitle: '', button_text: 'Parcourir les cours' },
          es: { title: '', subtitle: '', button_text: 'Explorar cursos' },
          ja: { title: '', subtitle: '', button_text: 'コースを閲覧' }
        }
      });
      onUpdate();
    } catch (error) {
      toast.error('Failed to save banner');
    }
  };

  const startEdit = (banner) => {
    setEditingBanner(banner);
    setNewBanner({
      banner_index: banner.banner_index,
      image_url: banner.image_url,
      translations: {
        en: { title: banner.title || '', subtitle: banner.subtitle || '', button_text: banner.button_text || 'Browse Courses' },
        de: { title: '', subtitle: '', button_text: 'Kurse durchsuchen' },
        fr: { title: '', subtitle: '', button_text: 'Parcourir les cours' },
        es: { title: '', subtitle: '', button_text: 'Explorar cursos' },
        ja: { title: '', subtitle: '', button_text: 'コースを閲覧' }
      }
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Banner Management</CardTitle>
          <CardDescription>Manage rotating homepage banners with multilingual content</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {banners.map((banner) => (
              <div key={banner.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-4">
                  <img src={banner.image_url} alt="Banner" className="w-16 h-10 object-cover rounded" />
                  <div>
                    <h4 className="font-medium">Banner {banner.banner_index + 1}</h4>
                    <p className="text-sm text-gray-600">{banner.title}</p>
                  </div>
                </div>
                <Button onClick={() => startEdit(banner)} size="sm">Edit</Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{editingBanner ? 'Edit Banner' : 'Add New Banner'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Banner Position</Label>
            <Select 
              value={newBanner.banner_index.toString()} 
              onValueChange={(value) => setNewBanner({...newBanner, banner_index: parseInt(value)})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Banner 1</SelectItem>
                <SelectItem value="1">Banner 2</SelectItem>
                <SelectItem value="2">Banner 3</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Image URL</Label>
            <Input
              value={newBanner.image_url}
              onChange={(e) => setNewBanner({...newBanner, image_url: e.target.value})}
              placeholder="https://example.com/image.jpg"
            />
          </div>

          {['en', 'de', 'fr', 'es', 'ja'].map((lang) => (
            <div key={lang} className="border p-4 rounded-lg">
              <h4 className="font-medium mb-2">{lang.toUpperCase()} Translation</h4>
              <div className="space-y-2">
                <Input
                  placeholder="Title"
                  value={newBanner.translations[lang]?.title || ''}
                  onChange={(e) => setNewBanner({
                    ...newBanner,
                    translations: {
                      ...newBanner.translations,
                      [lang]: { ...newBanner.translations[lang], title: e.target.value }
                    }
                  })}
                />
                <Input
                  placeholder="Subtitle"
                  value={newBanner.translations[lang]?.subtitle || ''}
                  onChange={(e) => setNewBanner({
                    ...newBanner,
                    translations: {
                      ...newBanner.translations,
                      [lang]: { ...newBanner.translations[lang], subtitle: e.target.value }
                    }
                  })}
                />
                <Input
                  placeholder="Button Text"
                  value={newBanner.translations[lang]?.button_text || ''}
                  onChange={(e) => setNewBanner({
                    ...newBanner,
                    translations: {
                      ...newBanner.translations,
                      [lang]: { ...newBanner.translations[lang], button_text: e.target.value }
                    }
                  })}
                />
              </div>
            </div>
          ))}

          <div className="flex gap-2">
            <Button onClick={handleSaveBanner}>
              {editingBanner ? 'Update Banner' : 'Create Banner'}
            </Button>
            {editingBanner && (
              <Button variant="outline" onClick={() => {
                setEditingBanner(null);
                setNewBanner({
                  banner_index: 0,
                  image_url: '',
                  translations: {
                    en: { title: '', subtitle: '', button_text: 'Browse Courses' },
                    de: { title: '', subtitle: '', button_text: 'Kurse durchsuchen' },
                    fr: { title: '', subtitle: '', button_text: 'Parcourir les cours' },
                    es: { title: '', subtitle: '', button_text: 'Explorar cursos' },
                    ja: { title: '', subtitle: '', button_text: 'コースを閲覧' }
                  }
                });
              }}>
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const PaymentSuccess = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <CardTitle className="text-2xl text-green-600">Payment Successful!</CardTitle>
          <CardDescription>
            Congratulations! You have successfully enrolled in the course.
          </CardDescription>
        </CardHeader>
        <CardFooter className="flex gap-4">
          <Button onClick={() => navigate('/my-courses')} className="flex-1" data-testid="go-to-my-courses">
            View My Courses
          </Button>
          <Button variant="outline" onClick={() => navigate('/courses')} className="flex-1">
            Browse More Courses
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

// Footer Component
const Footer = () => {
  const { language, t } = useUser();
  const [legalPages, setLegalPages] = useState([]);

  useEffect(() => {
    loadLegalPages();
  }, [language]);

  const loadLegalPages = async () => {
    try {
      const response = await api.get(`/legal?language=${language}`);
      setLegalPages(response.data);
    } catch (error) {
      console.error('Failed to load legal pages:', error);
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300 py-12 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1">
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_polyglot-learn-31/artifacts/x629raf6_Zeichenfla%CC%88che%201.png" 
                alt="21xacademy" 
                className="h-8 w-auto"
              />
              <h3 className="text-xl font-bold text-white">21xacademy</h3>
            </div>
            <p className="text-sm mb-4">
              {t('company_description', 'AI & Business Development learning platform by 21x GmbH. Specialized in cutting-edge technology and business consulting education.')}
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-gray-400 hover:text-white">
                <span className="sr-only">LinkedIn</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-white">
                <span className="sr-only">Twitter</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="col-span-1">
            <h4 className="text-lg font-semibold text-white mb-4">{t('quick_links', 'Quick Links')}</h4>
            <ul className="space-y-2">
              <li><Link to="/courses" className="hover:text-white transition-colors">{t('courses', 'Courses')}</Link></li>
              <li><Link to="/about" className="hover:text-white transition-colors">{t('about', 'About')}</Link></li>
              <li><Link to="/how-it-works" className="hover:text-white transition-colors">{t('how_it_works', 'How It Works')}</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div className="col-span-1">
            <h4 className="text-lg font-semibold text-white mb-4">{t('support', 'Support')}</h4>
            <ul className="space-y-2">
              <li><a href="mailto:support@21xacademy.com" className="hover:text-white transition-colors">{t('contact_support', 'Contact Support')}</a></li>
              <li><a href="mailto:help@21xacademy.com" className="hover:text-white transition-colors">{t('help_center', 'Help Center')}</a></li>
              <li><a href="#newsletter" className="hover:text-white transition-colors">{t('newsletter', 'Newsletter')}</a></li>
            </ul>
          </div>

          {/* Legal */}
          <div className="col-span-1">
            <h4 className="text-lg font-semibold text-white mb-4">{t('legal', 'Legal')}</h4>
            <ul className="space-y-2">
              {legalPages.map((page) => (
                <li key={page.id}>
                  <Link 
                    to={`/legal/${page.page_type}`} 
                    className="hover:text-white transition-colors"
                  >
                    {page.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-700 text-center">
          <p className="text-sm">
            © {new Date().getFullYear()} 21xacademy by 21x GmbH. {t('all_rights_reserved', 'All rights reserved.')}
          </p>
          <p className="text-xs mt-2">
            {t('platform_description', 'AI & Business Development learning platform with multilingual support')}
          </p>
        </div>
      </div>
    </footer>
  );
};

// Legal Page Component
const LegalPageView = () => {
  const { pageType } = useParams();
  const { language } = useUser();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPage();
  }, [pageType, language]);

  const loadPage = async () => {
    try {
      const response = await api.get(`/legal/${pageType}?language=${language}`);
      setPage(response.data);
    } catch (error) {
      console.error('Failed to load legal page:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-2xl font-bold text-center">Page not found</h1>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl font-bold mb-8">{page.title}</h1>
      <div 
        className="prose prose-lg max-w-none"
        dangerouslySetInnerHTML={{ __html: page.content }}
      />
    </div>
  );
};

// Feature Management Component
const FeatureManagement = ({ features, onUpdate }) => {
  const [editingFeature, setEditingFeature] = useState(null);
  const [newFeature, setNewFeature] = useState({
    order_index: 0,
    icon_name: 'Globe',
    color_scheme: 'blue',
    translations: {
      en: { title: '', description: '' },
      de: { title: '', description: '' },
      fr: { title: '', description: '' },
      es: { title: '', description: '' },
      ja: { title: '', description: '' }
    }
  });

  const handleSaveFeature = async () => {
    try {
      if (editingFeature) {
        await api.put(`/admin/cms/features/${editingFeature.id}`, newFeature);
        toast.success('Feature card updated successfully!');
      } else {
        await api.post('/admin/cms/features', newFeature);
        toast.success('Feature card created successfully!');
      }
      
      setEditingFeature(null);
      setNewFeature({
        order_index: 0,
        icon_name: 'Globe',
        color_scheme: 'blue',
        translations: {
          en: { title: '', description: '' },
          de: { title: '', description: '' },
          fr: { title: '', description: '' },
          es: { title: '', description: '' },
          ja: { title: '', description: '' }
        }
      });
      onUpdate();
    } catch (error) {
      toast.error('Failed to save feature card');
    }
  };

  const handleDeleteFeature = async (featureId) => {
    if (window.confirm('Are you sure you want to delete this feature card?')) {
      try {
        await api.delete(`/admin/cms/features/${featureId}`);
        toast.success('Feature card deleted successfully!');
        onUpdate();
      } catch (error) {
        toast.error('Failed to delete feature card');
      }
    }
  };

  const startEdit = (feature) => {
    setEditingFeature(feature);
    setNewFeature({
      order_index: feature.order_index,
      icon_name: feature.icon_name,
      color_scheme: feature.color_scheme,
      translations: {
        en: { title: feature.title || '', description: feature.description || '' },
        de: { title: '', description: '' },
        fr: { title: '', description: '' },
        es: { title: '', description: '' },
        ja: { title: '', description: '' }
      }
    });
  };

  const cancelEdit = () => {
    setEditingFeature(null);
    setNewFeature({
      order_index: 0,
      icon_name: 'Globe',
      color_scheme: 'blue',
      translations: {
        en: { title: '', description: '' },
        de: { title: '', description: '' },
        fr: { title: '', description: '' },
        es: { title: '', description: '' },
        ja: { title: '', description: '' }
      }
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Feature Cards Management</CardTitle>
          <CardDescription>Customize the "Why Choose LearnStream" feature cards</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Current Feature Cards</h3>
            {features.map((feature) => (
              <div key={feature.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${feature.color_scheme}-100`}>
                    <span className="text-sm">{feature.icon_name}</span>
                  </div>
                  <div>
                    <h4 className="font-medium">{feature.title}</h4>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                    <p className="text-xs text-gray-500">Position: {feature.order_index + 1}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{feature.color_scheme}</Badge>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => startEdit(feature)}
                  >
                    Edit
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleDeleteFeature(feature.id)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{editingFeature ? 'Edit Feature Card' : 'Add New Feature Card'}</CardTitle>
          <CardDescription>
            {editingFeature ? 'Update the existing feature card' : 'Create a new feature card for the homepage'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Position</Label>
              <Select 
                value={newFeature.order_index.toString()} 
                onValueChange={(value) => setNewFeature({...newFeature, order_index: parseInt(value)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Feature 1</SelectItem>
                  <SelectItem value="1">Feature 2</SelectItem>
                  <SelectItem value="2">Feature 3</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Icon</Label>
              <Select 
                value={newFeature.icon_name} 
                onValueChange={(value) => setNewFeature({...newFeature, icon_name: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Globe">Globe</SelectItem>
                  <SelectItem value="Clock">Clock</SelectItem>
                  <SelectItem value="Award">Award</SelectItem>
                  <SelectItem value="Users">Users</SelectItem>
                  <SelectItem value="Book">Book</SelectItem>
                  <SelectItem value="Star">Star</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Color Scheme</Label>
              <Select 
                value={newFeature.color_scheme} 
                onValueChange={(value) => setNewFeature({...newFeature, color_scheme: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="blue">Blue</SelectItem>
                  <SelectItem value="green">Green</SelectItem>
                  <SelectItem value="purple">Purple</SelectItem>
                  <SelectItem value="orange">Orange</SelectItem>
                  <SelectItem value="red">Red</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {['en', 'de', 'fr', 'es', 'ja'].map((lang) => (
            <div key={lang} className="border p-4 rounded-lg">
              <h4 className="font-medium mb-2">{lang.toUpperCase()} Translation</h4>
              <div className="space-y-2">
                <Input
                  placeholder="Title"
                  value={newFeature.translations[lang]?.title || ''}
                  onChange={(e) => setNewFeature({
                    ...newFeature,
                    translations: {
                      ...newFeature.translations,
                      [lang]: { ...newFeature.translations[lang], title: e.target.value }
                    }
                  })}
                />
                <Textarea
                  placeholder="Description"
                  value={newFeature.translations[lang]?.description || ''}
                  onChange={(e) => setNewFeature({
                    ...newFeature,
                    translations: {
                      ...newFeature.translations,
                      [lang]: { ...newFeature.translations[lang], description: e.target.value }
                    }
                  })}
                />
              </div>
            </div>
          ))}

          <div className="flex gap-2">
            <Button onClick={handleSaveFeature} className="bg-blue-600 hover:bg-blue-700">
              {editingFeature ? 'Update Feature Card' : 'Create Feature Card'}
            </Button>
            {editingFeature && (
              <Button variant="outline" onClick={cancelEdit}>
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Page Content Management Component
const PageContentManagement = ({ onUpdate }) => {
  const [selectedPage, setSelectedPage] = useState('about');
  const [pageTranslations, setPageTranslations] = useState({});
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadPageTranslations();
  }, [selectedPage]);

  const loadPageTranslations = async () => {
    try {
      // Load translations for all languages
      const languages = ['en', 'de', 'fr', 'es', 'ja'];
      const responses = await Promise.all(
        languages.map(lang => api.get(`/translations/${lang}`))
      );
      
      // Define page keys
      const pageKeys = {
        'about': [
          'about_us', 'about_subtitle', 'our_mission', 'mission_text', 
          'vision_text', 'our_values', 'accessibility', 'accessibility_desc',
          'quality', 'quality_desc', 'innovation', 'innovation_desc'
        ],
        'how_it_works': [
          'how_it_works_subtitle', 'getting_started', 'step_1_title', 'step_1_desc',
          'step_2_title', 'step_2_desc', 'step_3_title', 'step_3_desc',
          'platform_features', 'multilingual_content', 'multilingual_content_desc',
          'progress_tracking', 'progress_tracking_desc', 'lifetime_access', 'lifetime_access_desc'
        ]
      };
      
      const relevantKeys = pageKeys[selectedPage] || [];
      const translationsData = {};
      
      // Organize translations by key and language
      relevantKeys.forEach(key => {
        translationsData[key] = {};
        languages.forEach((lang, index) => {
          const translations = responses[index].data;
          translationsData[key][lang] = translations[key] || '';
        });
      });
      
      setPageTranslations(translationsData);
    } catch (error) {
      console.error('Failed to load page translations:', error);
    }
  };

  const handleUpdateTranslation = (key, language, value) => {
    setPageTranslations(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [language]: value
      }
    }));
  };

  const handleSaveTranslations = async () => {
    setLoading(true);
    try {
      // Prepare bulk translations update
      const translationsUpdate = {};
      Object.keys(pageTranslations).forEach(key => {
        Object.keys(pageTranslations[key]).forEach(lang => {
          if (!translationsUpdate[lang]) {
            translationsUpdate[lang] = {};
          }
          translationsUpdate[lang][key] = pageTranslations[key][lang];
        });
      });

      await api.put('/admin/translations/bulk', translationsUpdate);
      toast.success('Page translations updated successfully!');
      onUpdate();
    } catch (error) {
      toast.error('Failed to save translations');
    }
    setLoading(false);
  };

  const handleGenerateAITranslations = async () => {
    setGenerating(true);
    try {
      await api.post(`/admin/translations/generate?page_type=${selectedPage}&target_languages=de,fr,es,ja`);
      toast.success('AI translations generated successfully!');
      loadPageTranslations();
    } catch (error) {
      toast.error('Failed to generate AI translations');
    }
    setGenerating(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Page Content Management</CardTitle>
          <CardDescription>Manage translations for static pages like About Us and How It Works</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Page Selection */}
            <div>
              <Label>Select Page</Label>
              <Select value={selectedPage} onValueChange={setSelectedPage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="about">About Us</SelectItem>
                  <SelectItem value="how_it_works">How It Works</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* AI Translation Generator */}
            <div className="bg-blue-50 rounded-lg p-6 space-y-4">
              <div>
                <h4 className="font-medium text-blue-900">🤖 AI Translation Generator</h4>
                <p className="text-sm text-blue-700 mt-1">
                  <strong>How it works:</strong> Uses English text as source → Generates AI translations for German, French, Spanish, and Japanese
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  ⚠️ Make sure English text is filled in before generating AI translations
                </p>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-blue-900">Generate translations for:</p>
                  <p className="text-xs text-blue-600">🇩🇪 German • 🇫🇷 French • 🇪🇸 Spanish • 🇯🇵 Japanese</p>
                </div>
                <Button 
                  onClick={handleGenerateAITranslations}
                  disabled={generating}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {generating ? '🔄 Generating...' : '✨ Generate AI Translations'}
                </Button>
              </div>
            </div>

            {/* Translation Editor */}
            <div className="space-y-6">
              {Object.keys(pageTranslations).map((key) => (
                <div key={key} className="border rounded-lg p-4">
                  <h4 className="font-medium mb-4 capitalize">{key.replace(/_/g, ' ')}</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* English */}
                    <div>
                      <Label>English</Label>
                      <Textarea
                        value={pageTranslations[key]?.en || ''}
                        onChange={(e) => handleUpdateTranslation(key, 'en', e.target.value)}
                        rows={3}
                      />
                    </div>

                    {/* German */}
                    <div>
                      <Label>German</Label>
                      <Textarea
                        value={pageTranslations[key]?.de || ''}
                        onChange={(e) => handleUpdateTranslation(key, 'de', e.target.value)}
                        rows={3}
                      />
                    </div>

                    {/* French */}
                    <div>
                      <Label>French</Label>
                      <Textarea
                        value={pageTranslations[key]?.fr || ''}
                        onChange={(e) => handleUpdateTranslation(key, 'fr', e.target.value)}
                        rows={3}
                      />
                    </div>

                    {/* Spanish */}
                    <div>
                      <Label>Spanish</Label>
                      <Textarea
                        value={pageTranslations[key]?.es || ''}
                        onChange={(e) => handleUpdateTranslation(key, 'es', e.target.value)}
                        rows={3}
                      />
                    </div>

                    {/* Japanese */}
                    <div className="md:col-span-2">
                      <Label>Japanese</Label>
                      <Textarea
                        value={pageTranslations[key]?.ja || ''}
                        onChange={(e) => handleUpdateTranslation(key, 'ja', e.target.value)}
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Button 
              onClick={handleSaveTranslations}
              disabled={loading}
              className="w-full"
            >
              {loading ? 'Saving...' : 'Save All Translations'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Newsletter Management Component  
const NewsletterManagement = ({ newsletters, subscribers, onUpdate }) => {
  const [newNewsletter, setNewNewsletter] = useState({
    title: '',
    subject: '',
    content: '',
    scheduled_date: null
  });

  const [newSubscriber, setNewSubscriber] = useState({
    email: '',
    first_name: '',
    last_name: '',
    tags: []
  });

  const handleCreateNewsletter = async () => {
    try {
      await api.post('/admin/newsletters', newNewsletter);
      toast.success('Newsletter created successfully!');
      setNewNewsletter({
        title: '',
        subject: '',
        content: '',
        scheduled_date: null
      });
      onUpdate();
    } catch (error) {
      toast.error('Failed to create newsletter');
    }
  };

  const handleSendNewsletter = async (newsletterId) => {
    try {
      await api.post(`/admin/newsletters/${newsletterId}/send`);
      toast.success('Newsletter sent successfully!');
      onUpdate();
    } catch (error) {
      toast.error('Failed to send newsletter');
    }
  };

  const handleAddSubscriber = async () => {
    try {
      await api.post('/admin/subscribers', newSubscriber);
      toast.success('Subscriber added successfully!');
      setNewSubscriber({
        email: '',
        first_name: '',
        last_name: '',
        tags: []
      });
      onUpdate();
    } catch (error) {
      toast.error('Failed to add subscriber');
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Newsletter Campaigns</CardTitle>
            <CardDescription>{newsletters.length} newsletters created</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {newsletters.map((newsletter) => (
                <div key={newsletter.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <h4 className="font-medium">{newsletter.title}</h4>
                    <p className="text-sm text-gray-600">{newsletter.subject}</p>
                    <Badge variant={newsletter.status === 'sent' ? 'success' : 'secondary'}>
                      {newsletter.status}
                    </Badge>
                  </div>
                  {newsletter.status === 'draft' && (
                    <Button size="sm" onClick={() => handleSendNewsletter(newsletter.id)}>
                      Send
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Subscribers</CardTitle>
            <CardDescription>{subscribers.length} total subscribers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {subscribers.map((subscriber) => (
                <div key={subscriber.id} className="flex items-center justify-between p-2 border rounded">
                  <div>
                    <p className="font-medium text-sm">{subscriber.email}</p>
                    {subscriber.first_name && (
                      <p className="text-xs text-gray-600">{subscriber.first_name} {subscriber.last_name}</p>
                    )}
                  </div>
                  <Badge variant={subscriber.status === 'active' ? 'success' : 'secondary'} className="text-xs">
                    {subscriber.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Create Newsletter</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              placeholder="Newsletter Title"
              value={newNewsletter.title}
              onChange={(e) => setNewNewsletter({...newNewsletter, title: e.target.value})}
            />
            <Input
              placeholder="Email Subject"
              value={newNewsletter.subject}
              onChange={(e) => setNewNewsletter({...newNewsletter, subject: e.target.value})}
            />
            <Textarea
              placeholder="Newsletter Content (HTML supported)"
              rows={6}
              value={newNewsletter.content}
              onChange={(e) => setNewNewsletter({...newNewsletter, content: e.target.value})}
            />
            <Button onClick={handleCreateNewsletter}>Create Newsletter</Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Add Subscriber</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              placeholder="Email Address"
              type="email"
              value={newSubscriber.email}
              onChange={(e) => setNewSubscriber({...newSubscriber, email: e.target.value})}
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="First Name"
                value={newSubscriber.first_name}
                onChange={(e) => setNewSubscriber({...newSubscriber, first_name: e.target.value})}
              />
              <Input
                placeholder="Last Name"
                value={newSubscriber.last_name}
                onChange={(e) => setNewSubscriber({...newSubscriber, last_name: e.target.value})}
              />
            </div>
            <Input
              placeholder="Tags (comma separated)"
              value={newSubscriber.tags.join(', ')}
              onChange={(e) => setNewSubscriber({
                ...newSubscriber, 
                tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
              })}
            />
            <Button onClick={handleAddSubscriber}>Add Subscriber</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Site Settings Management Component
const SiteSettingsManagement = ({ settings, onUpdate }) => {
  const [newSettings, setNewSettings] = useState(settings);

  const handleUpdateSettings = async () => {
    try {
      await onUpdate(newSettings);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const updateSetting = (key, value) => {
    setNewSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Site Settings</CardTitle>
          <CardDescription>Customize colors, branding, and global settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-medium mb-4">Colors & Branding</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Primary Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={newSettings.primary_color || '#3B82F6'}
                    onChange={(e) => updateSetting('primary_color', e.target.value)}
                    className="w-16 h-10"
                  />
                  <Input
                    value={newSettings.primary_color || '#3B82F6'}
                    onChange={(e) => updateSetting('primary_color', e.target.value)}
                    placeholder="#3B82F6"
                  />
                </div>
              </div>

              <div>
                <Label>Secondary Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={newSettings.secondary_color || '#10B981'}
                    onChange={(e) => updateSetting('secondary_color', e.target.value)}
                    className="w-16 h-10"
                  />
                  <Input
                    value={newSettings.secondary_color || '#10B981'}
                    onChange={(e) => updateSetting('secondary_color', e.target.value)}
                    placeholder="#10B981"
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-4">Branding</h3>
            <div className="space-y-4">
              <div>
                <Label>Logo URL</Label>
                <Input
                  value={newSettings.logo_url || ''}
                  onChange={(e) => updateSetting('logo_url', e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
              </div>

              <div>
                <Label>Site Name</Label>
                <Input
                  value={newSettings.site_name || 'LearnStream'}
                  onChange={(e) => updateSetting('site_name', e.target.value)}
                />
              </div>

              <div>
                <Label>Site Description</Label>
                <Textarea
                  value={newSettings.site_description || ''}
                  onChange={(e) => updateSetting('site_description', e.target.value)}
                  placeholder="Multilingual learning platform with AI-powered translations"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-4">Contact Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={newSettings.contact_email || ''}
                  onChange={(e) => updateSetting('contact_email', e.target.value)}
                  placeholder="contact@learnstream.com"
                />
              </div>

              <div>
                <Label>Support Email</Label>
                <Input
                  type="email"
                  value={newSettings.support_email || ''}
                  onChange={(e) => updateSetting('support_email', e.target.value)}
                  placeholder="support@learnstream.com"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-4">Social Media</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Twitter URL</Label>
                <Input
                  value={newSettings.twitter_url || ''}
                  onChange={(e) => updateSetting('twitter_url', e.target.value)}
                  placeholder="https://twitter.com/learnstream"
                />
              </div>

              <div>
                <Label>LinkedIn URL</Label>
                <Input
                  value={newSettings.linkedin_url || ''}
                  onChange={(e) => updateSetting('linkedin_url', e.target.value)}
                  placeholder="https://linkedin.com/company/learnstream"
                />
              </div>
            </div>
          </div>

          <Button onClick={handleUpdateSettings}>Update Settings</Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Author Management Component
const AuthorManagement = ({ authors, onUpdate }) => {
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [newAuthor, setNewAuthor] = useState({
    name: '',
    bio: '',
    profile_image_url: '',
    expertise: [],
    credentials: [],
    social_links: {
      linkedin: '',
      twitter: '',
      website: ''
    },
    years_experience: ''
  });

  const handleSaveAuthor = async () => {
    try {
      const authorData = {
        ...newAuthor,
        expertise: typeof newAuthor.expertise === 'string' 
          ? newAuthor.expertise.split(',').map(s => s.trim()).filter(s => s)
          : newAuthor.expertise,
        credentials: typeof newAuthor.credentials === 'string'
          ? newAuthor.credentials.split(',').map(s => s.trim()).filter(s => s)
          : newAuthor.credentials,
        years_experience: newAuthor.years_experience ? parseInt(newAuthor.years_experience) : null
      };

      if (editingAuthor) {
        await api.put(`/admin/authors/${editingAuthor.id}`, authorData);
        toast.success('Author updated successfully');
      } else {
        await api.post('/admin/authors', authorData);
        toast.success('Author created successfully');
      }
      
      setEditingAuthor(null);
      setNewAuthor({
        name: '',
        bio: '',
        profile_image_url: '',
        expertise: [],
        credentials: [],
        social_links: { linkedin: '', twitter: '', website: '' },
        years_experience: ''
      });
      onUpdate();
    } catch (error) {
      toast.error('Failed to save author');
    }
  };

  const startEdit = (author) => {
    setEditingAuthor(author);
    setNewAuthor({
      name: author.name || '',
      bio: author.bio || '',
      profile_image_url: author.profile_image_url || '',
      expertise: Array.isArray(author.expertise) ? author.expertise.join(', ') : '',
      credentials: Array.isArray(author.credentials) ? author.credentials.join(', ') : '',
      social_links: {
        linkedin: author.social_links?.linkedin || '',
        twitter: author.social_links?.twitter || '',
        website: author.social_links?.website || ''
      },
      years_experience: author.years_experience?.toString() || ''
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Author Management</CardTitle>
          <CardDescription>Manage course authors and their profiles</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Author Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={newAuthor.name}
                  onChange={(e) => setNewAuthor({...newAuthor, name: e.target.value})}
                  placeholder="Dr. Jane Smith"
                />
              </div>

              <div>
                <Label>Years of Experience</Label>
                <Input
                  type="number"
                  value={newAuthor.years_experience}
                  onChange={(e) => setNewAuthor({...newAuthor, years_experience: e.target.value})}
                  placeholder="15"
                />
              </div>

              <div className="md:col-span-2">
                <Label>Bio</Label>
                <Textarea
                  value={newAuthor.bio}
                  onChange={(e) => setNewAuthor({...newAuthor, bio: e.target.value})}
                  placeholder="Professional background and expertise..."
                  rows={4}
                />
              </div>

              <div>
                <Label>Profile Image URL</Label>
                <Input
                  value={newAuthor.profile_image_url}
                  onChange={(e) => setNewAuthor({...newAuthor, profile_image_url: e.target.value})}
                  placeholder="https://example.com/profile.jpg"
                />
              </div>

              <div>
                <Label>Expertise (comma-separated)</Label>
                <Input
                  value={newAuthor.expertise}
                  onChange={(e) => setNewAuthor({...newAuthor, expertise: e.target.value})}
                  placeholder="Machine Learning, Python, Data Science"
                />
              </div>

              <div className="md:col-span-2">
                <Label>Credentials (comma-separated)</Label>
                <Input
                  value={newAuthor.credentials}
                  onChange={(e) => setNewAuthor({...newAuthor, credentials: e.target.value})}
                  placeholder="PhD Computer Science, Google Cloud Professional"
                />
              </div>

              <div>
                <Label>LinkedIn URL</Label>
                <Input
                  value={newAuthor.social_links.linkedin}
                  onChange={(e) => setNewAuthor({
                    ...newAuthor, 
                    social_links: {...newAuthor.social_links, linkedin: e.target.value}
                  })}
                  placeholder="https://linkedin.com/in/janesmith"
                />
              </div>

              <div>
                <Label>Twitter URL</Label>
                <Input
                  value={newAuthor.social_links.twitter}
                  onChange={(e) => setNewAuthor({
                    ...newAuthor, 
                    social_links: {...newAuthor.social_links, twitter: e.target.value}
                  })}
                  placeholder="https://twitter.com/janesmith"
                />
              </div>
            </div>

            <Button onClick={handleSaveAuthor}>
              {editingAuthor ? 'Update Author' : 'Create Author'}
            </Button>

            {/* Authors List */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Existing Authors</h3>
              {authors.map((author) => (
                <div key={author.id} className="flex items-start justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium">{author.name}</h4>
                    <p className="text-sm text-gray-600 mb-2">{author.bio}</p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {author.expertise?.map((skill, index) => (
                        <Badge key={index} variant="secondary">{skill}</Badge>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">
                      {author.years_experience} years experience • {author.total_courses || 0} courses • {author.total_students || 0} students
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => startEdit(author)}
                  >
                    Edit
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Legal Page Management Component
const LegalPageManagement = ({ legalPages, onUpdate }) => {
  const [editingPage, setEditingPage] = useState(null);
  const [newPage, setNewPage] = useState({
    page_type: '',
    order_index: 0,
    translations: {
      en: { title: '', content: '' },
      de: { title: '', content: '' },
      fr: { title: '', content: '' },
      es: { title: '', content: '' },
      ja: { title: '', content: '' }
    }
  });

  const handleSavePage = async () => {
    try {
      if (editingPage) {
        await api.put(`/admin/legal-pages/${editingPage.id}`, newPage);
        toast.success('Legal page updated successfully');
      } else {
        await api.post('/admin/legal-pages', newPage);
        toast.success('Legal page created successfully');
      }
      
      setEditingPage(null);
      setNewPage({
        page_type: '',
        order_index: 0,
        translations: {
          en: { title: '', content: '' },
          de: { title: '', content: '' },
          fr: { title: '', content: '' },
          es: { title: '', content: '' },
          ja: { title: '', content: '' }
        }
      });
      onUpdate();
    } catch (error) {
      toast.error('Failed to save legal page');
    }
  };

  const startEdit = (page) => {
    setEditingPage(page);
    setNewPage({
      page_type: page.page_type || '',
      order_index: page.order_index || 0,
      translations: {
        en: { title: page.title || '', content: page.content || '' },
        de: { title: '', content: '' },
        fr: { title: '', content: '' },
        es: { title: '', content: '' },
        ja: { title: '', content: '' }
      }
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Legal Pages Management</CardTitle>
          <CardDescription>Manage legal pages like Terms of Service, Privacy Policy, etc.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Page Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Page Type</Label>
                <Select 
                  value={newPage.page_type} 
                  onValueChange={(value) => setNewPage({...newPage, page_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select page type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="terms">Terms of Service</SelectItem>
                    <SelectItem value="privacy">Privacy Policy</SelectItem>
                    <SelectItem value="refund">Refund Policy</SelectItem>
                    <SelectItem value="cookies">Cookie Policy</SelectItem>
                    <SelectItem value="copyright">Copyright Policy</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Order Index</Label>
                <Input
                  type="number"
                  value={newPage.order_index}
                  onChange={(e) => setNewPage({...newPage, order_index: parseInt(e.target.value)})}
                  placeholder="0"
                />
              </div>

              <div className="md:col-span-2">
                <Label>Title (English)</Label>
                <Input
                  value={newPage.translations.en.title}
                  onChange={(e) => setNewPage({
                    ...newPage,
                    translations: {
                      ...newPage.translations,
                      en: { ...newPage.translations.en, title: e.target.value }
                    }
                  })}
                  placeholder="Terms of Service"
                />
              </div>

              <div className="md:col-span-2">
                <Label>Content (English) - HTML Supported</Label>
                <Textarea
                  value={newPage.translations.en.content}
                  onChange={(e) => setNewPage({
                    ...newPage,
                    translations: {
                      ...newPage.translations,
                      en: { ...newPage.translations.en, content: e.target.value }
                    }
                  })}
                  placeholder="<h2>1. Acceptance of Terms</h2><p>By accessing and using this platform...</p>"
                  rows={10}
                />
              </div>
            </div>

            <Button onClick={handleSavePage}>
              {editingPage ? 'Update Legal Page' : 'Create Legal Page'}
            </Button>

            {/* Legal Pages List */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Existing Legal Pages</h3>
              {legalPages.map((page) => (
                <div key={page.id} className="flex items-start justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium capitalize">{page.page_type?.replace('_', ' ')}</h4>
                    <p className="text-sm text-gray-600">{page.title}</p>
                    <p className="text-xs text-gray-500">Order: {page.order_index}</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => startEdit(page)}
                  >
                    Edit
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Video Management Component - YouTube-style video management
const VideoManagement = ({ courses, onUpdate }) => {
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [videos, setVideos] = useState([]);
  const [editingVideo, setEditingVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    video_url: '',
    duration_seconds: '',
    thumbnail_url: '',
    keywords: [],
    video_type: 'lesson',
    is_free_preview: false,
    order_index: 0
  });

  useEffect(() => {
    if (selectedCourseId) {
      loadCourseVideos();
    }
  }, [selectedCourseId]);

  const loadCourseVideos = async () => {
    try {
      const response = await api.get(`/admin/courses/${selectedCourseId}/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to load course videos:', error);
      toast.error('Failed to load videos');
    }
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setVideoForm({
      title: video.translations?.en?.title || '',
      description: video.translations?.en?.description || '',
      video_url: video.video_url || '',
      duration_seconds: video.duration_seconds?.toString() || '',
      thumbnail_url: video.thumbnail_url || '',
      keywords: video.keywords || [],
      video_type: video.video_type || 'lesson',
      is_free_preview: video.is_free_preview || false,
      order_index: video.order_index || 0
    });
  };

  const handleSaveVideo = async () => {
    if (!editingVideo) return;
    
    setLoading(true);
    try {
      await api.put(`/admin/videos/${editingVideo.id}`, {
        ...videoForm,
        duration_seconds: parseInt(videoForm.duration_seconds),
        keywords: typeof videoForm.keywords === 'string' 
          ? videoForm.keywords.split(',').map(k => k.trim()).filter(k => k)
          : videoForm.keywords
      });
      
      toast.success('Video updated successfully!');
      setEditingVideo(null);
      setVideoForm({
        title: '',
        description: '',
        video_url: '',
        duration_seconds: '',
        thumbnail_url: '',
        keywords: [],
        video_type: 'lesson',
        is_free_preview: false,
        order_index: 0
      });
      loadCourseVideos();
      onUpdate();
    } catch (error) {
      toast.error('Failed to update video');
    }
    setLoading(false);
  };

  const handleToggleVideoVisibility = async (videoId, isVisible) => {
    try {
      await api.put(`/admin/videos/${videoId}/visibility?is_visible=${!isVisible}`);
      toast.success(`Video ${!isVisible ? 'made visible' : 'hidden'} successfully!`);
      loadCourseVideos();
    } catch (error) {
      toast.error('Failed to toggle video visibility');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (window.confirm('Are you sure you want to delete this video? This action cannot be undone.')) {
      try {
        await api.delete(`/admin/videos/${videoId}`);
        toast.success('Video deleted successfully!');
        loadCourseVideos();
        onUpdate();
      } catch (error) {
        toast.error('Failed to delete video');
      }
    }
  };

  const getVideoPlaceholderThumbnail = (videoType) => {
    const thumbnails = {
      'lesson': 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=60',
      'intro': 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=60',
      'summary': 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=60',
      'bonus': 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=60'
    };
    return thumbnails[videoType] || thumbnails['lesson'];
  };

  return (
    <div className="space-y-6">
      {/* Course Selection */}
      <Card>
        <CardHeader>
          <CardTitle>📹 Video Management</CardTitle>
          <CardDescription>Manage videos for each course - edit, organize, and control visibility</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label>Select Course to Manage Videos</Label>
              <Select value={selectedCourseId} onValueChange={setSelectedCourseId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a course to manage its videos" />
                </SelectTrigger>
                <SelectContent>
                  {courses.map(course => (
                    <SelectItem key={course.id} value={course.id}>
                      {course.title || 'Untitled Course'} ({course.video_count} videos)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Video List for Selected Course */}
      {selectedCourseId && (
        <Card>
          <CardHeader>
            <CardTitle>Course Videos</CardTitle>
            <CardDescription>
              {videos.length} videos in {courses.find(c => c.id === selectedCourseId)?.title || 'this course'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {videos.map((video) => (
                <div key={video.id} className="border rounded-lg p-4">
                  <div className="flex items-start gap-4">
                    {/* Video Thumbnail */}
                    <div className="w-32 h-20 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={video.thumbnail_url || getVideoPlaceholderThumbnail(video.video_type)}
                        alt={video.translations?.en?.title || 'Video thumbnail'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Video Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium">{video.translations?.en?.title || 'Untitled Video'}</h4>
                          <p className="text-sm text-gray-600 mt-1">{video.translations?.en?.description || 'No description'}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Badge variant="outline">#{video.order_index + 1}</Badge>
                            <Badge variant="secondary">{video.video_type}</Badge>
                            <Badge variant={video.is_visible ? "default" : "outline"}>
                              {video.is_visible ? "👁️ Visible" : "🙈 Hidden"}
                            </Badge>
                            {video.is_free_preview && (
                              <Badge variant="success">🆓 Free Preview</Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Duration: {Math.floor(video.duration_seconds / 60)}m {video.duration_seconds % 60}s
                          </p>
                          {video.keywords && video.keywords.length > 0 && (
                            <p className="text-xs text-gray-500">
                              Keywords: {video.keywords.join(', ')}
                            </p>
                          )}
                        </div>
                        
                        {/* Video Controls */}
                        <div className="flex flex-col gap-2">
                          {/* Visibility Toggle */}
                          <div className="flex items-center space-x-2">
                            <span className="text-xs">{video.is_visible ? 'Visible' : 'Hidden'}</span>
                            <button
                              onClick={() => handleToggleVideoVisibility(video.id, video.is_visible)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                video.is_visible ? 'bg-blue-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                  video.is_visible ? 'translate-x-5' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                          
                          <div className="flex gap-1">
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditVideo(video)}
                            >
                              Edit
                            </Button>
                            <Button 
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteVideo(video.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {videos.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No videos found for this course. Use the "Add Video" tab to add videos.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Video Edit Form */}
      {editingVideo && (
        <Card>
          <CardHeader>
            <CardTitle>📝 Edit Video: {editingVideo.translations?.en?.title}</CardTitle>
            <CardDescription>Update video metadata and settings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Video Title</Label>
                  <Input
                    value={videoForm.title}
                    onChange={(e) => setVideoForm({...videoForm, title: e.target.value})}
                    placeholder="Enter video title"
                  />
                </div>
                
                <div>
                  <Label>Duration (seconds)</Label>
                  <Input
                    type="number"
                    value={videoForm.duration_seconds}
                    onChange={(e) => setVideoForm({...videoForm, duration_seconds: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Video Type</Label>
                  <Select 
                    value={videoForm.video_type} 
                    onValueChange={(value) => setVideoForm({...videoForm, video_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lesson">📚 Lesson</SelectItem>
                      <SelectItem value="intro">👋 Introduction</SelectItem>
                      <SelectItem value="summary">📋 Summary</SelectItem>
                      <SelectItem value="bonus">🎁 Bonus Content</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Order Position</Label>
                  <Input
                    type="number"
                    value={videoForm.order_index}
                    onChange={(e) => setVideoForm({...videoForm, order_index: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={videoForm.description}
                  onChange={(e) => setVideoForm({...videoForm, description: e.target.value})}
                  rows={3}
                  placeholder="Enter video description"
                />
              </div>
              
              <div>
                <Label>Video URL</Label>
                <Input
                  value={videoForm.video_url}
                  onChange={(e) => setVideoForm({...videoForm, video_url: e.target.value})}
                  placeholder="https://example.com/video.mp4"
                />
              </div>
              
              <div>
                <Label>Video Thumbnail</Label>
                <div className="space-y-3">
                  {/* Thumbnail Preview */}
                  {videoForm.thumbnail_url && (
                    <div className="flex items-center gap-4">
                      <img 
                        src={videoForm.thumbnail_url} 
                        alt="Video thumbnail preview" 
                        className="w-32 h-20 object-cover rounded-lg border"
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setVideoForm({...videoForm, thumbnail_url: ''})}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <label className="block">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors cursor-pointer">
                      <svg className="mx-auto h-8 w-8 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <p className="text-sm text-gray-600">Upload video thumbnail</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        if (e.target.files[0]) {
                          try {
                            const formData = new FormData();
                            formData.append('file', e.target.files[0]);
                            
                            const response = await api.post('/admin/upload/video-thumbnail', formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            
                            setVideoForm({...videoForm, thumbnail_url: response.data.image_url});
                            toast.success('Thumbnail uploaded successfully!');
                          } catch (error) {
                            toast.error('Failed to upload thumbnail');
                          }
                        }
                      }}
                    />
                  </label>
                  
                  {/* Manual URL Input */}
                  <Input
                    value={videoForm.thumbnail_url}
                    onChange={(e) => setVideoForm({...videoForm, thumbnail_url: e.target.value})}
                    placeholder="Or enter thumbnail URL manually"
                  />
                </div>
              </div>
              
              <div>
                <Label>Keywords (comma-separated)</Label>
                <Input
                  value={Array.isArray(videoForm.keywords) ? videoForm.keywords.join(', ') : videoForm.keywords}
                  onChange={(e) => setVideoForm({...videoForm, keywords: e.target.value})}
                  placeholder="AI, machine learning, business strategy"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_free_preview"
                  checked={videoForm.is_free_preview}
                  onChange={(e) => setVideoForm({...videoForm, is_free_preview: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="is_free_preview">Allow as free preview (visible without enrollment)</Label>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleSaveVideo}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? 'Updating...' : 'Update Video'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setEditingVideo(null);
                    setVideoForm({
                      title: '', description: '', video_url: '', duration_seconds: '',
                      thumbnail_url: '', keywords: [], video_type: 'lesson',
                      is_free_preview: false, order_index: 0
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Professional Checkout Component
const Checkout = () => {
  const { user, language, t } = useUser();
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(user ? 2 : 1);
  const [processing, setProcessing] = useState(false);
  
  const [checkoutForm, setCheckoutForm] = useState({
    email: user?.email || '',
    name: user?.name || '',
    paymentMethod: 'stripe'
  });

  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
    
    // Set step based on auth status
    setCurrentStep(user ? 2 : 1);
  }, [courseId, user]);

  const loadCourse = async () => {
    try {
      const response = await api.get(`/courses/${courseId}?language=${language}`);
      setCourse(response.data);
    } catch (error) {
      toast.error('Course not found');
      navigate('/courses');
    }
    setLoading(false);
  };

  const handleSocialLogin = () => {
    const redirectUrl = encodeURIComponent(`${window.location.origin}/checkout/${courseId}`);
    const authUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
    window.location.href = `${authUrl}/?redirect=${redirectUrl}`;
  };

  const handlePurchase = async () => {
    if (!user) {
      toast.error('Please sign in first');
      return;
    }

    setProcessing(true);
    try {
      const response = await api.post('/payments/create-session', {
        course_id: courseId,
        payment_method: checkoutForm.paymentMethod
      });
      
      // Redirect to payment
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to process payment');
    }
    setProcessing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading checkout...</div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Course not found.</p>
      </div>
    );
  }

  const steps = [
    { id: 1, name: user ? 'Review' : 'Sign In', completed: !!user },
    { id: 2, name: 'Course Details', completed: currentStep > 2 },
    { id: 3, name: 'Payment', completed: false }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <nav aria-label="Checkout progress">
            <ol className="flex items-center justify-center space-x-5">
              {steps.map((step, stepIdx) => (
                <li key={step.id} className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                      step.completed || step.id === currentStep
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'border-gray-300 text-gray-500'
                    }`}
                  >
                    {step.completed ? (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      step.id
                    )}
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    step.completed || step.id === currentStep ? 'text-blue-600' : 'text-gray-500'
                  }`}>
                    {step.name}
                  </span>
                  {stepIdx < steps.length - 1 && (
                    <div className="ml-5 w-8 h-0.5 bg-gray-300"></div>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>
                  {!user ? '🔐 Sign In to Continue' : '💳 Complete Your Purchase'}
                </CardTitle>
                <CardDescription>
                  {!user ? 'Sign in to access your courses and complete the purchase' : 'Review your order and complete the payment'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!user ? (
                  // Step 1: Authentication
                  <div className="space-y-4">
                    <Button
                      className="w-full flex items-center justify-center gap-3 py-3 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                      onClick={handleSocialLogin}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </Button>
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">or</span>
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate('/auth')}
                    >
                      Sign In with Email
                    </Button>
                  </div>
                ) : (
                  // Step 2: Payment Details
                  <div className="space-y-6">
                    {/* Customer Info */}
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-2">👤 Customer Information</h4>
                      <p className="text-green-700">{user.name}</p>
                      <p className="text-green-600 text-sm">{user.email}</p>
                    </div>
                    
                    {/* Payment Method Selection */}
                    <div>
                      <Label className="text-base font-semibold">💳 Payment Method</Label>
                      <div className="mt-3 space-y-3">
                        <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="payment"
                            value="stripe"
                            checked={checkoutForm.paymentMethod === 'stripe'}
                            onChange={(e) => setCheckoutForm({...checkoutForm, paymentMethod: e.target.value})}
                            className="mr-3"
                          />
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                            </div>
                            <div>
                              <p className="font-medium">Credit Card</p>
                              <p className="text-sm text-gray-600">Visa, Mastercard, American Express</p>
                            </div>
                          </div>
                        </label>
                        
                        <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="payment"
                            value="paypal"
                            checked={checkoutForm.paymentMethod === 'paypal'}
                            onChange={(e) => setCheckoutForm({...checkoutForm, paymentMethod: e.target.value})}
                            className="mr-3"
                          />
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-yellow-500 rounded flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.248-.92 4.604-4.068 6.197-7.916 6.197h-1.607c-.524 0-.968.382-1.05.9L8.05 20.96a.641.641 0 0 0 .633.74h3.445c.524 0 .968-.382 1.05-.9l.488-3.096c.082-.518.526-.9 1.05-.9h.659c3.186 0 5.659-1.292 6.389-5.032.302-1.544.141-2.83-.562-3.756z"/>
                              </svg>
                            </div>
                            <div>
                              <p className="font-medium">PayPal</p>
                              <p className="text-sm text-gray-600">Pay with your PayPal account</p>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    {/* Purchase Button */}
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 text-lg"
                      onClick={handlePurchase}
                      disabled={processing}
                    >
                      {processing ? (
                        <div className="flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                          </svg>
                          Processing...
                        </div>
                      ) : (
                        `Complete Purchase - $${course.price}`
                      )}
                    </Button>
                    
                    {/* Trust Indicators */}
                    <div className="text-center space-y-2">
                      <div className="flex items-center justify-center gap-4 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span>Secure Payment</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>30-day Guarantee</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span>Instant Access</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Your purchase is protected by our 30-day money-back guarantee
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8">
              <CardHeader>
                <CardTitle className="text-lg">📋 Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Course Info */}
                <div className="flex gap-3">
                  <img 
                    src={course.thumbnail_url || getCoursePlaceholderImage(course.category)}
                    alt={course.title}
                    className="w-20 h-12 object-cover rounded"
                  />
                  <div>
                    <h4 className="font-medium text-sm line-clamp-2">{course.title}</h4>
                    <p className="text-xs text-gray-600">{course.category}</p>
                    <p className="text-xs text-gray-600">{course.video_count} videos</p>
                  </div>
                </div>
                
                {/* Authors */}
                {course.authors && course.authors.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">👨‍🏫 Instructors:</p>
                    {course.authors.slice(0, 2).map((author) => (
                      <div key={author.id} className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs">
                          {author.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <span className="text-sm text-gray-700">{author.name}</span>
                        {author.is_verified && (
                          <svg className="w-3 h-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Price Breakdown */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm">Course Price:</span>
                    <span className="font-medium">${course.price}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-green-600">
                    <span>Savings:</span>
                    <span>Lifetime Access (Value: $999)</span>
                  </div>
                  <div className="border-t mt-2 pt-2">
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span>Total:</span>
                      <span className="text-green-600">${course.price}</span>
                    </div>
                    <p className="text-xs text-gray-500 text-center mt-1">One-time payment</p>
                  </div>
                </div>
                
                {/* Guarantee Badge */}
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <div className="flex items-center justify-center gap-2 text-blue-700">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span className="font-medium">30-Day Money-Back Guarantee</span>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">Not satisfied? Get a full refund within 30 days</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <UserProvider>
        <BrowserRouter>
          <Navbar />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/courses/:courseId" element={<CourseDetail />} />
            <Route path="/my-courses" element={<MyCourses />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/legal/:pageType" element={<LegalPageView />} />
            <Route path="/checkout/:courseId" element={<Checkout />} />
          </Routes>
          <Footer />
          <Toaster position="top-right" />
        </BrowserRouter>
      </UserProvider>
    </div>
  );
}

export default App;